import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { 
    Menu, 
    X, 
    Search, 
    LayoutDashboard, 
    LogOut, 
    User,
    ChevronDown,
    Map,
    Building2,
    Users,
    BarChart3,
    FolderOpen,
    Newspaper,
    Handshake,
    Globe,
    Upload,
    BookOpen,
    Shield,
    GraduationCap
} from 'lucide-react';

const Header = () => {
    const { user, isAuthenticated, login, logout } = useAuth();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Navigation structure for authenticated users
    const resourcesLinks = [
        { href: '/welcome', label: 'Getting Started', icon: BookOpen },
        { href: '/directory', label: 'Public Directory', icon: Building2 },
        { href: '/partners', label: 'Partner Databases', icon: Globe },
        { href: '/map', label: 'Map', icon: Map },
        { href: '/members', label: 'Member Database', icon: Users },
        { href: '/impact', label: 'Impact', icon: BarChart3 },
        { href: '/news', label: 'Resource Library', icon: Newspaper },
        { href: '/getting-started', label: 'Getting Started Tutorial', icon: GraduationCap },
    ];

    const contributeLinks = [
        { href: '/nominate', label: 'Nominate a Member', icon: Handshake },
        { href: '/add-resource', label: 'Add a Resource', icon: Upload },
        { href: '/submit-metrics', label: 'Submit Impact Metrics', icon: BarChart3 },
        { href: '/my-submissions', label: 'My Submissions', icon: FolderOpen },
    ];

    // Public nav links (for non-authenticated users)
    const publicNavLinks = [
        { href: '/partners', label: 'Partner Databases' },
        { href: '/apply', label: 'Apply For Membership' },
    ];

    const isActive = (path) => location.pathname === path;
    const isInGroup = (links) => links.some(link => location.pathname === link.href);

    return (
        <header className="glass-header sticky top-0 z-50" data-testid="main-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="flex items-center gap-2 group"
                        data-testid="logo-link"
                    >
                        <img 
                            src="https://customer-assets.emergentagent.com/job_partner-network-11/artifacts/e97jx98f_HAAPE%20Rainbow%20logo.jpg" 
                            alt="HAAPE" 
                            className="h-10 w-10 rounded-lg object-cover"
                        />
                        <span className="font-heading font-light text-xl text-foreground">
                            HAAPE Hub for Employment & Support
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
                        {isAuthenticated ? (
                            <>
                                {/* Resources Dropdown */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button 
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-1 ${
                                                isInGroup(resourcesLinks)
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                            }`}
                                            data-testid="resources-dropdown"
                                        >
                                            Resources
                                            <ChevronDown className="h-4 w-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-56">
                                        {resourcesLinks.map((link) => (
                                            <DropdownMenuItem key={link.href} asChild>
                                                <Link 
                                                    to={link.href} 
                                                    className={`flex items-center gap-2 cursor-pointer ${
                                                        isActive(link.href) ? 'bg-primary/10 text-primary' : ''
                                                    }`}
                                                >
                                                    <link.icon className="h-4 w-4" />
                                                    {link.label}
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Contribute Dropdown */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button 
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 flex items-center gap-1 ${
                                                isInGroup(contributeLinks)
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                            }`}
                                            data-testid="contribute-dropdown"
                                        >
                                            Contribute
                                            <ChevronDown className="h-4 w-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-56">
                                        {contributeLinks.map((link) => (
                                            <DropdownMenuItem key={link.href} asChild>
                                                <Link 
                                                    to={link.href} 
                                                    className={`flex items-center gap-2 cursor-pointer ${
                                                        isActive(link.href) ? 'bg-primary/10 text-primary' : ''
                                                    }`}
                                                >
                                                    <link.icon className="h-4 w-4" />
                                                    {link.label}
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            // Public nav for non-authenticated users
                            publicNavLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                                        isActive(link.href)
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    {link.label}
                                </Link>
                            ))
                        )}
                    </nav>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-3">
                        {isAuthenticated && (
                            <Link to="/directory">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="gap-2"
                                    data-testid="search-btn"
                                >
                                    <Search className="h-4 w-4" />
                                    Search
                                </Button>
                            </Link>
                        )}

                        {isAuthenticated && user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="gap-2"
                                        data-testid="user-menu-btn"
                                    >
                                        {user.picture ? (
                                            <img 
                                                src={user.picture} 
                                                alt={user.name}
                                                className="h-6 w-6 rounded-full"
                                            />
                                        ) : (
                                            <User className="h-4 w-4" />
                                        )}
                                        <span className="max-w-[100px] truncate">{user.name}</span>
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem asChild>
                                        <Link 
                                            to="/profile" 
                                            className="flex items-center gap-2 cursor-pointer"
                                            data-testid="profile-link"
                                        >
                                            <User className="h-4 w-4" />
                                            My Profile
                                        </Link>
                                    </DropdownMenuItem>
                                    {user.is_admin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link 
                                                    to="/admin" 
                                                    className="flex items-center gap-2 cursor-pointer"
                                                    data-testid="admin-dashboard-link"
                                                >
                                                    <LayoutDashboard className="h-4 w-4" />
                                                    Admin Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {(user.role === 'chapter_director' || user.is_admin) && (
                                        <>
                                            {!user.is_admin && <DropdownMenuSeparator />}
                                            <DropdownMenuItem asChild>
                                                <Link 
                                                    to="/chapter-dashboard" 
                                                    className="flex items-center gap-2 cursor-pointer"
                                                    data-testid="chapter-dashboard-link"
                                                >
                                                    <Shield className="h-4 w-4 text-amber-500" />
                                                    Chapter Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={logout}
                                        className="flex items-center gap-2 cursor-pointer text-destructive"
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button 
                                onClick={login}
                                size="sm"
                                data-testid="login-btn"
                            >
                                Member Login
                            </Button>
                        )}
                    </div>

                    {/* Mobile Menu */}
                    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                        <SheetTrigger asChild className="md:hidden">
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                {mobileMenuOpen ? (
                                    <X className="h-5 w-5" />
                                ) : (
                                    <Menu className="h-5 w-5" />
                                )}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-72">
                            <nav className="flex flex-col gap-2 mt-8">
                                {isAuthenticated ? (
                                    <>
                                        {/* Resources Section */}
                                        <div className="mb-4">
                                            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Resources
                                            </p>
                                            {resourcesLinks.map((link) => (
                                                <Link
                                                    key={link.href}
                                                    to={link.href}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                                                        isActive(link.href)
                                                            ? 'bg-primary/10 text-primary'
                                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                    }`}
                                                >
                                                    <link.icon className="h-4 w-4" />
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>

                                        {/* Contribute Section */}
                                        <div className="mb-4">
                                            <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Contribute
                                            </p>
                                            {contributeLinks.map((link) => (
                                                <Link
                                                    key={link.href}
                                                    to={link.href}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                                                        isActive(link.href)
                                                            ? 'bg-primary/10 text-primary'
                                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                    }`}
                                                >
                                                    <link.icon className="h-4 w-4" />
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    publicNavLinks.map((link) => (
                                        <Link
                                            key={link.href}
                                            to={link.href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`px-4 py-3 rounded-md text-base font-medium transition-colors duration-150 ${
                                                isActive(link.href)
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                            }`}
                                        >
                                            {link.label}
                                        </Link>
                                    ))
                                )}
                                
                                <div className="border-t border-border pt-4 mt-4">
                                    {isAuthenticated && user ? (
                                        <>
                                            <div className="px-4 py-2 flex items-center gap-3">
                                                {user.picture && (
                                                    <img 
                                                        src={user.picture} 
                                                        alt={user.name}
                                                        className="h-8 w-8 rounded-full"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium text-sm">{user.name}</p>
                                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                                </div>
                                            </div>
                                            <Link
                                                to="/profile"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="flex items-center gap-2 px-4 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                                            >
                                                <User className="h-5 w-5" />
                                                My Profile
                                            </Link>
                                            {user.is_admin && (
                                                <Link
                                                    to="/admin"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="flex items-center gap-2 px-4 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                                                >
                                                    <LayoutDashboard className="h-5 w-5" />
                                                    Admin Dashboard
                                                </Link>
                                            )}
                                            {(user.role === 'chapter_director' || user.is_admin) && (
                                                <Link
                                                    to="/chapter-dashboard"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="flex items-center gap-2 px-4 py-3 text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                                                    data-testid="mobile-chapter-dashboard-link"
                                                >
                                                    <Shield className="h-5 w-5 text-amber-500" />
                                                    Chapter Dashboard
                                                </Link>
                                            )}
                                            <button
                                                onClick={() => {
                                                    logout();
                                                    setMobileMenuOpen(false);
                                                }}
                                                className="flex items-center gap-2 px-4 py-3 text-base font-medium text-destructive hover:bg-destructive/10 rounded-md w-full"
                                            >
                                                <LogOut className="h-5 w-5" />
                                                Logout
                                            </button>
                                        </>
                                    ) : (
                                        <Button 
                                            onClick={() => {
                                                login();
                                                setMobileMenuOpen(false);
                                            }}
                                            className="w-full"
                                        >
                                            Member Login
                                        </Button>
                                    )}
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
